import type { AssetsRecommendation } from '@/modules/Asset/interface/Recommendation';
import type { Author } from '@/modules/Author/interface/Author';
import type { ProgramFile } from '@/modules/Entertainment/interface/Program';
import type { CardInfo } from '@/modules/Search/interface/Search';

export type Content = {
  related_links?: RelatedLink[];
  audio_urls: {
    low: string | null;
    medium: string | null;
    high: string | null;
  };
  author_id: string;
  author_name: string;
  author_slug: string;
  author_thumb: string;
  bookmarked: boolean;
  count_in_progress: boolean;
  description: string;
  description_striped: string;
  display_order: number | null;
  files: ProgramFile[];
  handsup_action: string | null;
  handsup_label: string | null;
  handsup_product: string | null;
  campaign_id: string | null;
  has_access: boolean;
  has_audio: boolean;
  has_live: boolean;
  has_presentation: boolean;
  has_text: boolean;
  has_video: boolean;
  id: string;
  live_embed?: string;
  live_id?: string;
  live_start_at: string | null;
  live_url: string | null;
  next_content: string;
  object: 'content';
  other_authors: Omit<Author, 'portfolios'>[];
  portfolios: AssetsRecommendation[];
  presentation: string | null;
  previous_content: string;
  program_id: string;
  program_slug: string;
  program_title: string;
  program_thumb: string;
  program_type: string;
  program_type_trans: string;
  published_at: string;
  report: string | null;
  seasons: Season[];
  show_open_account_button: boolean;
  slug: string;
  summary: string;
  summary_striped: string;
  tags: string[];
  thumb: string;
  title: string;
  transcription: unknown;
  type: string;
  video_urls: {
    low: string | null;
    medium: string | null;
    high: string | null;
    default: string | null;
  };
  view_time: string;
  watch: WatchContent | null;
};

export type RelatedLink = {
  title: string;
  url: string;
  display_order: number | null;
};

export type WatchContent = {
  total: string;
  progress: string;
  percentage: number;
};

export type Season = {
  id: string;
  contents: SeasonContent[];
  display_order: number | null;
  summary: string | null;
  title: string;
};

export type SeasonContent = Pick<
  Content,
  | 'author_id'
  | 'author_name'
  | 'author_thumb'
  | 'bookmarked'
  | 'count_in_progress'
  | 'has_access'
  | 'has_audio'
  | 'has_live'
  | 'has_text'
  | 'has_video'
  | 'id'
  | 'live_embed'
  | 'live_id'
  | 'live_start_at'
  | 'live_url'
  | 'object'
  | 'other_authors'
  | 'program_id'
  | 'program_slug'
  | 'program_title'
  | 'program_type'
  | 'program_type_trans'
  | 'published_at'
  | 'slug'
  | 'summary'
  | 'thumb'
  | 'title'
  | 'type'
  | 'view_time'
> & {
  card_info: Omit<
    CardInfo,
    'content_count' | 'thumb_horizontal' | 'thumb_vertical'
  >;
  duration: string;
  is_next: boolean;
};

export type ImageURLs = {
  logo: string;
  thumb: string;
  bg_desktop: string;
  bg_mobile: string;
};

export enum ContentModeEnum {
  AUDIO = 'audio',
  SLIDE = 'slide',
  TEXT = 'text',
  VIDEO = 'video',
}

export type ContentMode = `${ContentModeEnum}`;
